import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Crown, RefreshCw, Target, TrendingUp, Award, Briefcase, Info } from "lucide-react";
import { useCurrency } from "@/context/CurrencyContext";
import Money from "@/components/Money";
import PageHeader from "@/components/PageHeader";

export default function Executive() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const { currency } = useCurrency();
  const from = (data?.currency || "USD").toUpperCase();

  useEffect(() => { load(); }, []);
  const load = async () => { setLoading(true); try { const r = await api.get("/executive/assessment"); setData(r.data.data); } catch { /* need profile */ } finally { setLoading(false); } };
  const refresh = async () => { setRefreshing(true); try { const r = await api.post("/executive/assessment/refresh"); setData(r.data.data); toast.success("Assessment refreshed"); } catch (e) { toast.error(e.response?.data?.detail || "Failed"); } finally { setRefreshing(false); } };

  if (loading) return <div className="p-10">Assessing executive readiness...</div>;
  if (!data) return (
    <div className="p-10 max-w-4xl" data-testid="executive-page">
      <div className="font-mono text-xs uppercase tracking-widest text-muted-foreground mb-2">Module 14</div>
      <h1 className="font-display font-bold text-5xl tracking-tight mb-6">Executive Coaching</h1>
      <div className="card-flat p-10 text-center">
        <Crown size={40} className="mx-auto text-primary mb-4"/>
        <p className="text-muted-foreground mb-6">Board readiness, exec comp benchmarks, equity guidance. Complete your profile first, then run the assessment.</p>
        <button data-testid="exec-generate" onClick={refresh} className="inline-flex items-center justify-center rounded-md bg-primary text-primary-foreground hover:bg-primary/90 font-semibold transition-colors px-6 py-3">Run executive assessment</button>
      </div>
    </div>
  );

  return (
    <div className="p-10 max-w-6xl" data-testid="executive-page">
      <div className="flex justify-between items-start mb-8">
        <div>
          <div className="font-mono text-xs uppercase tracking-widest text-muted-foreground mb-2">Module 14</div>
          <h1 className="font-display font-bold text-5xl tracking-tight">Executive Coaching</h1>
        </div>
        <button data-testid="exec-refresh" onClick={refresh} disabled={refreshing} className="inline-flex items-center justify-center rounded-md border border-border bg-background hover:bg-accent text-foreground transition-colors px-4 py-2 text-sm flex items-center gap-2"><RefreshCw size={14} className={refreshing?"animate-spin":""}/> Refresh</button>
      </div>

      {(data.is_converted_estimate || (data.currency && data.currency.toUpperCase() !== currency)) && (
        <div className="mb-4 flex items-start gap-2 rounded-lg border border-[hsl(var(--warning))/0.4] bg-[hsl(var(--warning))/0.08] text-[hsl(var(--warning))] px-4 py-3 text-sm" data-testid="exec-converted-notice">
          <Info size={14} className="shrink-0 mt-0.5" />
          <div><strong>Converted estimate.</strong> AI returned amounts in {from}; converted to {currency} for display.</div>
        </div>
      )}

      <div className="card-flat p-8 mb-6 grid md:grid-cols-[auto_1fr] gap-8 items-center" data-testid="exec-headline">
        <ScoreDial score={data.board_readiness_score}/>
        <div>
          <div className="text-xs font-mono uppercase tracking-widest text-primary mb-2">Board readiness</div>
          <div className="font-display font-bold text-3xl tracking-tight mb-2">{data.readiness_verdict}</div>
          <div className="text-sm text-foreground/80 leading-relaxed">{data.commentary}</div>
          <div className="mt-3 inline-flex items-center gap-2 text-xs font-mono uppercase tracking-widest text-muted-foreground border border-border px-3 py-1"><Award size={12}/> Board seat: {data.board_seat_readiness}</div>
        </div>
      </div>

      {/* Dimensions */}
      <div className="grid md:grid-cols-5 gap-px bg-border border border-border mb-6" data-testid="exec-dimensions">
        {Object.entries(data.dimensions || {}).map(([k,v]) => (
          <div key={k} className="bg-background p-4">
            <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground mb-2">{k.replace(/_/g,' ')}</div>
            <div className="font-display font-bold text-2xl">{v}<span className="text-sm text-muted-foreground font-mono">/100</span></div>
            <div className="h-1 bg-secondary mt-2"><div className="h-full bg-primary" style={{ width: `${v}%` }}/></div>
          </div>
        ))}
      </div>

      {/* Next role + Equity */}
      <div className="grid md:grid-cols-2 gap-4 mb-6">
        <div className="card-flat p-6" data-testid="exec-next-role">
          <div className="text-xs font-mono uppercase tracking-widest text-muted-foreground mb-3 flex items-center gap-2"><Target size={12}/> Next target role</div>
          <div className="font-display font-bold text-2xl tracking-tight mb-3">{data.next_role?.title}</div>
          <div className="grid grid-cols-2 gap-3 text-sm">
            <Kv label="Base" value={<Money value={data.next_role?.typical_comp?.base} from={from} compact />}/>
            <Kv label="Bonus" value={`${data.next_role?.typical_comp?.bonus_pct}%`}/>
            <Kv label="Equity/yr" value={<Money value={data.next_role?.typical_comp?.equity_value} from={from} compact />}/>
            <Kv label="Total target" value={<Money value={data.next_role?.typical_comp?.total_target_comp} from={from} compact />} accent/>
          </div>
          <div className="mt-3 text-xs font-mono text-muted-foreground">Time to reach: {data.next_role?.time_to_reach_years} years</div>
        </div>
        <div className="card-flat p-6" data-testid="exec-equity">
          <div className="text-xs font-mono uppercase tracking-widest text-muted-foreground mb-3 flex items-center gap-2"><Briefcase size={12}/> Equity guidance</div>
          <div className="font-display font-bold text-2xl tracking-tight mb-1"><Money value={data.equity_guidance?.expected_grant_value} from={from} compact /></div>
          <div className="text-xs font-mono text-muted-foreground mb-3">{data.equity_guidance?.vesting_schedule}</div>
          <ul className="space-y-2">
            {(data.equity_guidance?.negotiation_tips||[]).map((t,i) => <li key={i} className="text-sm text-foreground/80 flex gap-2"><span className="w-1 h-1 bg-emerald-500 mt-2 shrink-0"/> {t}</li>)}
          </ul>
        </div>
      </div>

      {/* Gaps + Action plans */}
      <div className="grid md:grid-cols-3 gap-4" data-testid="exec-plans">
        <Panel title="Gaps to close" items={data.gaps} icon={TrendingUp} tone="amber"/>
        <Panel title="90-day plan" items={data.action_plan_90d} icon={Target} tone="blue"/>
        <Panel title="12-month plan" items={data.action_plan_12m} icon={Target} tone="emerald"/>
      </div>
    </div>
  );
}

function ScoreDial({ score }) {
  const pct = Math.max(0, Math.min(100, score));
  return (
    <div className="relative w-32 h-32">
      <svg viewBox="0 0 100 100" className="w-32 h-32">
        <circle cx="50" cy="50" r="45" stroke="#2a2a2a" strokeWidth="6" fill="none"/>
        <circle cx="50" cy="50" r="45" stroke="#0F52BA" strokeWidth="6" fill="none" strokeDasharray={`${(pct/100)*283} 283`} strokeLinecap="round" transform="rotate(-90 50 50)"/>
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <div className="font-display font-black text-4xl">{pct}</div>
        <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">exec</div>
      </div>
    </div>
  );
}
function Kv({ label, value, accent }) {
  return (
    <div className="border border-border p-2">
      <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">{label}</div>
      <div className={`font-display font-bold text-base mt-0.5 ${accent ? "text-emerald-400" : ""}`}>{value}</div>
    </div>
  );
}
function Panel({ title, items = [], icon: Icon, tone = "blue" }) {
  const c = { amber: "text-amber-400", blue: "text-primary", emerald: "text-emerald-400" }[tone];
  return (
    <div className="card-flat p-5">
      <div className={`flex items-center gap-2 text-xs font-mono uppercase tracking-widest ${c} mb-3`}><Icon size={12}/> {title}</div>
      <ul className="space-y-2">
        {items.map((it,i) => <li key={i} className="text-sm text-foreground/80 flex gap-2"><span className={`w-1 h-1 mt-2 shrink-0 bg-current ${c}`}/> {it}</li>)}
      </ul>
    </div>
  );
}
